import axios from "axios";
import { z } from "zod";
import { environment } from "../../../environment";
import { errorLog } from "../../../logger";
import { FartherAccountId, PerformanceGroupId, } from "../../Types";
import ValidationError from "../../ZodError";
const GraphDataItem = z.object({
    date: z.string(),
    balance: z.number(),
});
const GroupSettings = z.object({
    trailingOneYearReturn: z.boolean(),
    trailingThreeYearReturn: z.boolean(),
    trailingFiveYearReturn: z.boolean(),
    trailingTenYearReturn: z.boolean(),
    trailingOneYearVol: z.boolean(),
    trailingThreeYearVol: z.boolean(),
    trailingFiveYearVol: z.boolean(),
    trailingTenYearVol: z.boolean(),
    returnExpected: z.boolean(),
    forwardVolatility: z.boolean(),
    monteCarloFive: z.boolean(),
    monteCarloTen: z.boolean(),
    monteCarloFifty: z.boolean(),
    monteCarloNinety: z.boolean(),
    monteCarloNinetyFive: z.boolean(),
    finscore: z.boolean(),
    isToggledOn: z.boolean(),
});
const GroupType = z.enum([
    "AllClientAccounts",
    "AllFamilyAccounts",
    "CustomClient",
    "AdvisorBook",
    "AllFirm",
    "CustomFirm",
    "CustomAdvisor",
    "CustomFamily",
    "SingleAccount",
]);
const DateRage = z.object({
    endDate: z.string(), // YYYY-MM-DD
    startDate: z.string(), // YYYY-MM-DD
});
const DateRangePoint = z.object({
    beginningMarketValue: z.number(), // This is the value on the `dateRange.startDate` date
    containsHistory: z.boolean(),
    dateRange: DateRage.nullable(),
    endingMarketValue: z.number(), // This is the value on the `dateRange.endDate` date
    fees: z.number(),
    heldEntirePeriod: z.boolean(),
    netAdditions: z.number().nullable(),
    netGains: z.number().nullable(),
    timeWeightedReturns: z
        .object({
        returns: z.number(),
    })
        .nullable(),
});
const AccountGroup = z.object({
    accountGroupId: PerformanceGroupId,
    displayName: z.string(),
    dataVendor: z.string(),
    accountGroupType: GroupType,
    accountGroupVendorId: z.string(),
    modifyTime: z.string(),
    createTime: z.string(),
    virtualAccountIds: FartherAccountId.array(),
    dateRangePoints: DateRangePoint.array(),
    groupSettings: GroupSettings,
    balanceSummary: GraphDataItem.array().optional(), // You only get an entry for each `dateRanges.endDate`, will not return each day for range of dates
});
export const GetAccountGroupsRes = AccountGroup.array();
function getCacheStringFromDateRange(dateRanges) {
    if (dateRanges === null) {
        return "null";
    }
    if (dateRanges.length === 0) {
        return "null";
    }
    const dateRangesLength = dateRanges.length;
    const startDate = dateRanges[0]?.startDate ?? "";
    const endDate = dateRanges[dateRangesLength - 1]?.endDate ?? "";
    return `${startDate} -> ${endDate} (${dateRanges.length})`;
}
export const queryKey = (requestBody, _timeFrame, // use values in requestBody.dateAndReturnSettings.dateRanges
_groupId) => {
    return queryKeyAGs(requestBody);
};
export const queryKeyAGs = (requestBody) => {
    const dr = getCacheStringFromDateRange(requestBody.dateAndReturnSettings.dateRanges);
    return [
        requestBody.personId,
        url(),
        requestBody.dateAndReturnSettings.feeMethod,
        requestBody.dateAndReturnSettings.returnMethod,
        dr,
    ];
};
export const url = () => {
    return environment() === "PROD"
        ? "https://middleware.prod.farther.com/account-groups/group/get-account-groups"
        : "https://middleware.uat.farther.com/account-groups/group/get-account-groups";
};
export default async function getAccountGroups(requestBody, config) {
    const { data } = await axios.put(url(), requestBody, {
        headers: {
            Authorization: `Bearer ${config.jwt}`,
            ...("advisorId" in config && { Advisor: config.advisorId }),
            ...("clientId" in config && { Client: config.clientId }),
        },
    });
    const decoded = GetAccountGroupsRes.safeParse(data);
    if (decoded.success) {
        return decoded.data;
    }
    else {
        const errorMessage = "putAccountGroups returned unexpected data.";
        errorLog(errorMessage, decoded.error.message);
        throw new ValidationError(errorMessage, decoded.error.issues);
    }
}
//# sourceMappingURL=getAccountGroups.js.map